import {
  buildOrganizationNode,
  getSchemaIds,
} from 'utils/structuredData/organization';

const ATTENDANCE_MODE_MAP = {
  OnlineOnlyAttendanceMode: 'https://schema.org/OnlineEventAttendanceMode',
  OnlineTelelphoneAttendanceMode: 'https://schema.org/OnlineEventAttendanceMode',
  AllAttendanceMode: 'https://schema.org/MixedEventAttendanceMode',
  OfflineAttendanceMode: 'https://schema.org/OfflineEventAttendanceMode',
};

const STATUS_MAP = {
  EventScheduled: 'https://schema.org/EventScheduled',
  EventCompleted: 'https://schema.org/EventCompleted',
  EventCancelled: 'https://schema.org/EventCancelled',
  EventPostponed: 'https://schema.org/EventPostponed',
  EventMovedOnline: 'https://schema.org/EventMovedOnline',
};

const stripUndefined = obj => {
  Object.keys(obj).forEach(k => obj[k] === undefined && delete obj[k]);
  return obj;
};

const isOnlineAttendance = mode =>
  mode === 'OnlineOnlyAttendanceMode' ||
  mode === 'OnlineTelelphoneAttendanceMode';

const absolutizeUrl = (url, baseUrl) => {
  if (!url) return undefined;
  if (/^https?:\/\//i.test(url)) return url;
  if (baseUrl && url.startsWith('/')) return `${baseUrl}${url}`;
  return url;
};

const NON_TEMPORAL_STATUSES = [
  'EventCancelled',
  'EventPostponed',
  'EventMovedOnline',
];

// Lower-case substrings of organizer names that already represent the primary
// brand (Gooding Christie's). A partner whose name contains any of these is
// treated as a duplicate of the primary Organization @id rather than a
// separate co-organizer.
const PRIMARY_ORGANIZER_NAME_SUBSTRINGS = ['gooding', 'christie'];

const buildPostalAddress = address => {
  if (!address) return undefined;
  const node = stripUndefined({
    '@type': 'PostalAddress',
    streetAddress: address.streetAddress,
    addressLocality: address.addressLocality,
    addressRegion: address.addressRegion,
    postalCode: address.postalCode,
    addressCountry: address.addressCountry,
  });
  return Object.keys(node).length > 1 ? node : undefined;
};

const buildPlace = location => {
  if (!location) return undefined;
  const place = stripUndefined({
    '@type': 'Place',
    name: location.name,
    address: buildPostalAddress(location.address || location.mailingAddress),
    geo:
      location.venueLocation?.lat != null && location.venueLocation?.lon != null
        ? {
            '@type': 'GeoCoordinates',
            latitude: location.venueLocation.lat,
            longitude: location.venueLocation.lon,
          }
        : undefined,
  });
  return Object.keys(place).length > 1 ? place : undefined;
};

const extractAuctionDates = subEvents => {
  if (!Array.isArray(subEvents) || !subEvents.length) {
    return { startDate: null, endDate: null };
  }
  const auctionOnly = subEvents.filter(
    s =>
      s &&
      (!s.sys ||
        s.sys?.contentType?.sys?.id === 'subEventAuction' ||
        s.__typename === 'ContentfulSubEventAuction')
  );
  const events = auctionOnly.length ? auctionOnly : subEvents;
  const starts = events
    .map(e => e?.startDate)
    .filter(Boolean)
    .sort();
  const ends = events
    .map(e => e?.endDate)
    .filter(Boolean)
    .sort();
  return {
    startDate: starts[0] || null,
    endDate: ends[ends.length - 1] || null,
  };
};

const pickRegistrationUrl = ctaList => {
  if (!Array.isArray(ctaList)) return undefined;
  for (const cta of ctaList) {
    const url = cta?.action?.url;
    if (url) return url;
  }
  return undefined;
};

const isPrimaryOrganizerName = name => {
  if (!name) return false;
  const lower = name.toLowerCase();
  return PRIMARY_ORGANIZER_NAME_SUBSTRINGS.some(s => lower.includes(s));
};

const buildOrganizers = ({ orgId, partner }) => {
  const organizers = [{ '@id': orgId }];
  if (partner?.name && !isPrimaryOrganizerName(partner.name)) {
    organizers.push(
      stripUndefined({
        '@type': 'Organization',
        name: partner.name,
        contactPoint: partner.contactPoint
          ? stripUndefined({
              '@type': 'ContactPoint',
              email: partner.contactPoint.email,
              telephone: partner.contactPoint.telephone,
              contactType: 'customer service',
            })
          : undefined,
      })
    );
  }
  return organizers.length === 1 ? organizers[0] : organizers;
};

export const buildAuctionEventStructuredData = ({
  auctionData,
  slug,
  featureImageUrl,
  ctaList,
  pathname,
  siteUrl,
  siteName,
  siteDescription,
  logoUrl,
  alternateName,
  parentOrganization,
  contactPoint,
  address,
}) => {
  if (!auctionData?.name) return null;

  const baseUrl = String(siteUrl || '').replace(/\/$/, '');
  const url = slug
    ? `${baseUrl}/auction/${slug}`
    : `${baseUrl}${pathname || ''}`;
  const eventId = `${url}#event`;
  const { orgId } = getSchemaIds(siteUrl);

  const { startDate, endDate } = extractAuctionDates(auctionData.subEvents);
  const now = Date.now();
  const isPast =
    endDate && Number.isFinite(new Date(endDate).getTime())
      ? new Date(endDate).getTime() <= now
      : null;
  const dateBasedStatus =
    isPast === true
      ? 'https://schema.org/EventCompleted'
      : isPast === false
      ? 'https://schema.org/EventScheduled'
      : undefined;
  const eventStatus = NON_TEMPORAL_STATUSES.includes(auctionData.eventStatus)
    ? STATUS_MAP[auctionData.eventStatus]
    : dateBasedStatus || STATUS_MAP[auctionData.eventStatus];
  const eventAttendanceMode = ATTENDANCE_MODE_MAP[auctionData.eventAttendanceMode];

  const physicalPlace = buildPlace(auctionData.location);
  const registrationUrl = absolutizeUrl(pickRegistrationUrl(ctaList), baseUrl);
  const virtualLocation = registrationUrl
    ? { '@type': 'VirtualLocation', url: registrationUrl }
    : undefined;
  const onlineOnly = isOnlineAttendance(auctionData.eventAttendanceMode);

  let location;
  if (onlineOnly) {
    location = virtualLocation;
  } else if (physicalPlace && virtualLocation) {
    location = [physicalPlace, virtualLocation];
  } else {
    location = physicalPlace || virtualLocation;
  }

  const description =
    auctionData.description?.description ||
    auctionData.description?.childMarkdownRemark?.html;

  const offer = registrationUrl
    ? stripUndefined({
        '@type': 'Offer',
        url: registrationUrl,
        availability:
          isPast === true
            ? 'https://schema.org/SoldOut'
            : 'https://schema.org/InStock',
        price: 0,
        priceCurrency: auctionData.currency || undefined,
        validFrom: startDate || undefined,
      })
    : undefined;

  const organizer = buildOrganizers({
    orgId,
    partner: auctionData.organizer,
  });

  const eventNode = stripUndefined({
    '@type': 'Event',
    '@id': eventId,
    name: auctionData.name,
    description,
    url,
    image: featureImageUrl,
    startDate: startDate || undefined,
    endDate: endDate || undefined,
    eventStatus,
    eventAttendanceMode,
    location,
    organizer,
    performer: { '@id': orgId },
    offers: offer,
  });

  return {
    '@context': 'https://schema.org',
    '@graph': [
      buildOrganizationNode({
        siteUrl,
        siteName,
        description: siteDescription,
        logoUrl,
        alternateName,
        parentOrganization,
        contactPoint,
        address,
      }),
      eventNode,
    ],
  };
};
